import type { CologuardFormState } from '../cologuardFormData'
import { LAB_ACCOUNT_SYSTEMS } from '../formData'
import type { OrderPdfData, OrderPdfSection, AutoPopulateData } from './pdfTypes'
import { parseCityStateZip } from '../shared/formValidators'

export function mapCologuardToPdf(form: CologuardFormState, auto: AutoPopulateData): OrderPdfData {
  const shippingCsz = parseCityStateZip(form.shippingCityStateZip)

  const races = [
    form.raceWhite && 'White',
    form.raceBlack && 'Black/African American',
    form.raceAsian && 'Asian',
    form.raceNativeHawaiian && 'Native Hawaiian/Pacific Islander',
    form.raceAmericanIndian && 'American Indian/Alaska Native',
    form.raceOther && (form.raceOtherText ? `Other: ${form.raceOtherText}` : 'Other'),
  ].filter(Boolean) as string[]

  const icd10Codes = form.diagnosisText
    ? form.diagnosisText
        .split(/[;\n]/)
        .map((s) => s.trim())
        .filter(Boolean)
    : []

  const vendorSections: OrderPdfSection[] = []

  // Shipping information
  const shippingData: Record<string, string> = {}
  if (form.shippingAddress) shippingData['Shipping Address'] = form.shippingAddress
  if (form.poBoxApt) shippingData['PO Box / Apt'] = form.poBoxApt
  if (form.shippingCityStateZip) shippingData['City, State, ZIP'] = form.shippingCityStateZip
  if (form.phoneType) shippingData['Phone Type'] = form.phoneType
  if (form.languagePreference) shippingData['Language Preference'] = form.languagePreference
  if (Object.keys(shippingData).length > 0) {
    vendorSections.push({ title: 'Kit Shipping Information', type: 'keyValue', data: shippingData })
  }

  // Billing address (when different from shipping)
  if (!form.sameAsShipping) {
    const billingData: Record<string, string> = {}
    if (form.billingAddress) billingData['Billing Address'] = form.billingAddress
    if (form.billingCityStateZip) billingData['City, State, ZIP'] = form.billingCityStateZip
    if (Object.keys(billingData).length > 0) {
      vendorSections.push({ title: 'Billing Address', type: 'keyValue', data: billingData })
    }
  }

  // Demographics
  const demographicData: Record<string, string> = {}
  if (form.hispanicLatino)
    demographicData['Hispanic/Latino'] = form.hispanicLatino === 'yes' ? 'Yes' : 'No'
  if (races.length > 0) demographicData['Race'] = races.join(', ')
  if (Object.keys(demographicData).length > 0) {
    vendorSections.push({ title: 'Demographics', type: 'keyValue', data: demographicData })
  }

  // Insurance details
  if (form.billInsurance === 'yes') {
    const insuranceDetails: Record<string, string> = {}
    if (form.insuranceType) insuranceDetails['Insurance Type'] = form.insuranceType
    if (form.claimsSubmissionAddress)
      insuranceDetails['Claims Address'] = form.claimsSubmissionAddress
    if (form.subscriberIdPolicyNumber)
      insuranceDetails['Subscriber ID'] = form.subscriberIdPolicyNumber
    if (form.groupNumber) insuranceDetails['Group Number'] = form.groupNumber
    if (form.plan) insuranceDetails['Plan'] = form.plan
    if (form.policyholderDob) insuranceDetails['Policyholder DOB'] = form.policyholderDob
    if (form.priorAuthCode) insuranceDetails['Prior Auth Code'] = form.priorAuthCode
    if (Object.keys(insuranceDetails).length > 0) {
      vendorSections.push({ title: 'Insurance Details', type: 'keyValue', data: insuranceDetails })
    }
  }

  return {
    vendorName: 'Exact Sciences (Cologuard)',
    orderTitle: 'Colorectal Cancer Screening Requisition',
    patient: {
      firstName: auto.patient.firstName,
      middleName: auto.patient.middleName,
      lastName: auto.patient.lastName,
      dateOfBirth: auto.patient.dateOfBirth,
      sex: auto.patient.sexCode,
      // Patient email intentionally omitted from the Cologuard PDF
      email: '',
      phone: form.phoneType ? `${auto.patient.phone} (${form.phoneType})` : auto.patient.phone,
      address: [form.shippingAddress, form.poBoxApt].filter(Boolean).join(', '),
      city: shippingCsz.city,
      state: shippingCsz.state,
      zip: shippingCsz.zip,
      country: auto.patient.country,
      // Patient ID intentionally omitted from the Cologuard PDF
      patientId: '',
    },
    provider: {
      organizationName: auto.provider.organizationName,
      providerName: auto.provider.providerName,
      npi: auto.provider.npi,
      phone: auto.provider.orgPhone,
      fax: auto.provider.orgFax,
      address: auto.provider.orgAddress,
      city: auto.provider.orgCity,
      state: auto.provider.orgState,
      zip: auto.provider.orgZip,
      email: auto.provider.providerEmail,
      accountNumber: auto.provider.labAccountNumbers[LAB_ACCOUNT_SYSTEMS.EXACT_SCIENCES] || '',
    },
    insurance: {
      primaryCarrier: auto.insurance.primaryCarrier,
      primaryMemberId: auto.insurance.primaryMemberId,
      primaryGroup: auto.insurance.primaryGroup,
      policyholderName: auto.insurance.policyholderName,
      relationship: auto.insurance.relationship,
    },
    icd10Codes,
    selectedTests: [
      { code: 'COLOGUARD', name: 'Cologuard Colorectal Cancer Screening', category: 'Screening' },
    ],
    vendorSections,
    signatureDataUrl: form.providerSignatureDataUrl,
    signatureDate: form.providerSignatureDate,
  }
}
